/* =============================================================
   PIXKOM 1A — Cart AJAX (Task 2.4)

   Public API:  window.pkCart.add(variantId, qty)

   Strategy: LEVERAGE Dawn, don't rewrite.
   - Add-to-cart goes through /cart/add.js with the Section Rendering
     API (?sections via the request body). We swap the returned HTML
     for #CartDrawer + #cart-icon-bubble ourselves, then hand control
     back to Dawn's <cart-drawer> element via .open().
   - Quantity stepper + remove inside the drawer are Dawn's existing
     <cart-drawer-items> / <cart-remove-button> / <quantity-input>
     machinery (cart.js) — we do NOT touch that path.
   - The free-shipping progress bar lives INSIDE the re-rendered
     drawer section (snippets/cart-drawer.liquid), so BOTH the add
     path and Dawn's change path re-render it with fresh Liquid values
     automatically. A defensive JS recompute (pkUpdateProgress) is also
     wired to Dawn's cartUpdate pub/sub event as a belt-and-braces
     fallback in case a future template renders progress outside the
     swapped node.

   Loaded AFTER cart-drawer.js so <cart-drawer> is already defined.
   Degrades gracefully: with JS off, .pk-add stays a real
   {% form 'product' %} submit to /cart/add (full page reload).
   ============================================================= */
(function () {
  'use strict';

  var SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  // Lazily-created polite live region so a failed add (sold out /
  // over-inventory, HTTP 422) is announced instead of failing silently.
  function pkAnnounce(message) {
    var region = document.getElementById('pk-cart-status');
    if (!region) {
      region = document.createElement('p');
      region.id = 'pk-cart-status';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.className = 'visually-hidden';
      document.body.appendChild(region);
    }
    // Clear first so the same message re-announces on a repeated click.
    region.textContent = '';
    // Next frame to guarantee AT picks up the change.
    requestAnimationFrame(function () { region.textContent = message; });
  }

  // Pull the inner HTML of a given selector out of a section's rendered
  // HTML string (Section Rendering API returns full section markup).
  function sectionInnerHTML(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector(selector);
    return el ? el.innerHTML : null;
  }

  // Recompute the free-ship progress fill + message from a cart total
  // (cents). Defensive fallback only — normally the re-rendered Liquid
  // snippet already carries correct values.
  function pkUpdateProgress(totalPriceCents) {
    document.querySelectorAll('.pk-progress[data-threshold]').forEach(function (track) {
      var threshold = parseInt(track.getAttribute('data-threshold'), 10);
      if (!threshold) return;
      var total = typeof totalPriceCents === 'number'
        ? totalPriceCents
        : parseInt(track.getAttribute('data-total'), 10) || 0;

      track.setAttribute('data-total', total);
      var pct = Math.min(100, (total * 100) / threshold);

      var fill = track.querySelector('.pk-progress__fill');
      if (fill) fill.style.width = (Math.round(pct * 10) / 10) + '%';

      // .pk-progress__msg is the a11y state carrier — keep it truthful.
      var wrapper = track.closest('.pk-freeshipping');
      var msg = wrapper && wrapper.querySelector('.pk-progress__msg');
      if (msg) {
        var remaining = threshold - total;
        if (remaining > 0) {
          var euros = (remaining / 100).toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          msg.textContent = 'Nur noch ' + euros + ' € bis zum Gratisversand';
        } else {
          msg.textContent = '🎉 Gratisversand freigeschaltet';
        }
      }
    });
  }
  window.pkUpdateProgress = pkUpdateProgress;

  // Swap re-rendered section HTML into the live DOM.
  function renderSections(sections) {
    if (!sections) return;

    // Header cart pill: replace inner markup of #cart-icon-bubble.
    if (sections['cart-icon-bubble']) {
      var bubble = document.getElementById('cart-icon-bubble');
      var bubbleHTML = sectionInnerHTML(sections['cart-icon-bubble'], '.shopify-section')
        || sections['cart-icon-bubble'];
      if (bubble && bubbleHTML != null) bubble.innerHTML = bubbleHTML;
    }

    // Drawer body: replace inner markup of #CartDrawer.
    if (sections['cart-drawer']) {
      var cartDrawer = document.getElementById('CartDrawer');
      var drawerHTML = sectionInnerHTML(sections['cart-drawer'], '#CartDrawer');
      if (cartDrawer && drawerHTML != null) {
        cartDrawer.innerHTML = drawerHTML;
        // The swap destroys #CartDrawer-Overlay's click-to-close binding
        // (ESC/✕ survive, backdrop doesn't). Re-bind it exactly as Dawn's
        // cart-drawer.js:87 does after its own identical swap.
        var drawer = getCartDrawer();
        if (drawer) {
          var overlay = drawer.querySelector('#CartDrawer-Overlay');
          if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
        }
      }
    }
  }

  // window.pkCart.add — used by card quick-add + PDP.
  function add(variantId, qty) {
    qty = qty || 1;
    var drawer = getCartDrawer();

    return fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: qty }],
        sections: SECTIONS,
        sections_url: window.location.pathname,
      }),
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          // Sold out / over-inventory (HTTP 422) etc. — surface it.
          throw result.data;
        }

        var data = result.data;
        renderSections(data.sections);

        if (drawer) {
          drawer.classList.remove('is-empty');
          drawer.open();
        }

        // Belt-and-braces: refresh progress from the fresh total.
        return fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            pkUpdateProgress(cart.total_price);
            // publish/PUB_SUB_EVENTS are top-level globals from Dawn's
            // pubsub.js/constants.js (const → not on window; reach as
            // bare identifiers behind a typeof guard).
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'pk-cart',
                cartData: cart,
                variantId: variantId,
              });
            }
            return cart;
          })
          .catch(function () { /* progress refresh is best-effort */ });
      })
      .catch(function (err) {
        // Network/hard failure: send the user to the cart so nothing is
        // silently lost.
        if (err instanceof TypeError) {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          return Promise.reject(err);
        }
        // Cart API rejection (e.g. sold out / over inventory): announce
        // it politely so the "+" isn't a silent no-op.
        var msg = (err && err.description) || 'Leider nicht mehr verfügbar.';
        pkAnnounce(msg);
        return Promise.reject(err);
      });
  }

  window.pkCart = { add: add, updateProgress: pkUpdateProgress };

  // Intercept quick-add clicks/form submits from card-product.liquid.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('.pk-add');
    if (!btn || btn.disabled) return;
    var variantId = btn.dataset.variantId;
    if (!variantId) return;

    event.preventDefault();
    btn.classList.add('is-loading');
    add(variantId, 1)
      .then(function () {
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        setTimeout(function () { btn.classList.remove('is-added'); }, 1200);
      })
      .catch(function () {
        btn.classList.remove('is-loading');
      });
  });

  // Guard the no-JS product form: with JS on, cancel the native POST so
  // we never double-submit (click handler above already fired the AJAX
  // add). Only block when the form actually carries a quick-add button
  // WITH a variant id — otherwise the click handler bailed and the native
  // POST must remain the fallback (no dead button). With JS off, this
  // listener never runs and the form POSTs.
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form.classList || !form.classList.contains('pk-add-form')) return;
    var addBtn = form.querySelector('.pk-add[data-variant-id]');
    if (addBtn && addBtn.dataset.variantId) {
      event.preventDefault();
    }
  });

  // Cart page (Task 5.1): keep the „N Artikel" subline under the
  // title truthful — it sits outside the .js-contents Dawn re-renders.
  function pkUpdateCartCount(itemCount) {
    document.querySelectorAll('.pk-cartpage__count-number').forEach(function (el) {
      el.textContent = itemCount;
    });
  }

  // Mobile bottom-nav (Task 6.4): keep the Korb badge in sync. This is
  // a SEPARATE element from #cart-icon-bubble (can't share an id with
  // the header pill), so it isn't covered by the Section Rendering
  // swap in renderSections() above — update it from the same
  // cartUpdate pub/sub payload the cart-page count already uses below.
  function pkUpdateBottomNavBadge(itemCount) {
    document.querySelectorAll('[data-pk-cart-badge]').forEach(function (el) {
      el.textContent = itemCount;
      if (itemCount > 0) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });
  }

  // Bottom-nav Korb trigger: mirrors the header cart pill (Task 2.1 —
  // cart-drawer.js's setHeaderCartIconAccessibility only binds the
  // single #cart-icon-bubble id, so a second element needs its own
  // binding). Only intercepts when a <cart-drawer> exists (cart_type
  // == 'drawer'); otherwise the real href navigates to /cart.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-pk-cart-trigger]');
    if (!trigger) return;
    var drawer = getCartDrawer();
    if (!drawer) return; // no drawer configured — let the link navigate to /cart
    event.preventDefault();
    drawer.open(trigger);
  });

  // Keep progress honest after Dawn's own change/remove flow, which
  // re-renders the drawer section (progress snippet included) but we
  // also recompute defensively from the pub/sub payload.
  document.addEventListener('DOMContentLoaded', function () {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (payload) {
        var cartData = payload && payload.cartData;
        if (cartData && typeof cartData.item_count === 'number') {
          pkUpdateCartCount(cartData.item_count);
          pkUpdateBottomNavBadge(cartData.item_count);
        }
        if (payload && payload.source === 'pk-cart') return; // progress already handled
        if (cartData && typeof cartData.total_price === 'number') {
          pkUpdateProgress(cartData.total_price);
        }
      });
    }
  });

  // Cart page discount field (Task 5.1, .pk-summary__discount):
  // store the code via Shopify's /discount/CODE endpoint without
  // leaving the cart; it is applied at checkout. Delegated so it
  // survives section re-renders. No-JS fallback: the form itself
  // GETs /checkout?discount=CODE.
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form.classList || !form.classList.contains('pk-summary__discount')) return;
    event.preventDefault();

    var input = form.querySelector('.pk-summary__discount-input');
    var code = input && input.value.trim();
    if (!code) {
      if (input) input.focus();
      return;
    }

    fetch('/discount/' + encodeURIComponent(code))
      .then(function () {
        form.classList.add('is-applied');
        var btn = form.querySelector('.pk-summary__discount-btn');
        if (btn) btn.textContent = '✓';
        pkAnnounce('Rabattcode gespeichert – er wird an der Kasse angewendet.');
      })
      .catch(function () {
        pkAnnounce('Rabattcode konnte nicht gespeichert werden.');
      });
  });
})();
